import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { getVoiceProviderSetting, setVoiceProvider, VOICE_PROVIDERS } from '../../../services/settingsService';

const LABELS = { vapi: 'Vapi', retell: 'Retell' };

const SettingsTab = () => {
  const { user } = useAuth();
  const [setting, setSetting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [retellConfigured, setRetellConfigured] = useState(null);

  const load = async () => {
    setLoading(true);
    setSetting(await getVoiceProviderSetting());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/retell-web-call?health=1`, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
    }).then((r) => r.json()).then((d) => setRetellConfigured(Boolean(d?.configured))).catch(() => setRetellConfigured(false));
  }, []);

  const change = async (provider) => {
    if (provider === setting?.value) return;
    const ok = window.confirm(
      `Switch the voice provider to ${LABELS[provider]}?\n\nEvery NEW call after this will use ${LABELS[provider]}. Calls already in progress are not affected.`
    );
    if (!ok) return;
    setSaving(true);
    const { error } = await setVoiceProvider(provider, user?.id);
    setSaving(false);
    if (error) {
      toast.error(`Could not switch provider: ${error}`);
      return;
    }
    toast.success(`Voice provider is now ${LABELS[provider]}`);
    load();
  };

  const current = setting?.value ?? 'vapi';

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Voice provider</h2>
      <p className="text-sm text-gray-600 mb-4">
        Which platform handles new voice calls. Use this to fail over during a vendor outage. Takes effect on the next call, no deploy needed.
      </p>

      <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="group" aria-label="Voice provider">
        {VOICE_PROVIDERS.map((p) => (
          <button
            key={p}
            type="button"
            disabled={loading || saving}
            onClick={() => change(p)}
            aria-pressed={current === p}
            className={`px-5 py-2 text-sm font-medium ${
              current === p ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            } disabled:opacity-50`}
          >
            {LABELS[p]}
          </button>
        ))}
      </div>

      {current === 'retell' && retellConfigured === false && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          Retell is selected but the server has no RETELL_API_KEY / RETELL_BASE_AGENT_ID. New calls will fail until secrets are set.
        </div>
      )}

      <div className="mt-3 text-xs text-gray-500">
        {loading ? 'Loading…' : setting
          ? `Current: ${LABELS[current]} · last changed ${new Date(setting.updated_at).toLocaleString()}${setting.updated_by ? ` by ${setting.updated_by}` : ''}`
          : 'No setting row found; defaulting to Vapi.'}
      </div>
    </div>
  );
};

export default SettingsTab;
